import React, { useCallback } from 'react'
import Check from '../../../../Icon/icons/Check/index'
import FollowTag from '../../../shared/FollowTag/index'

export interface ConnectionCardProps {
  uid: string
  /** UUID for profile URL construction. Falls back to uid when absent. */
  uuid?: string
  displayName: string
  avatarUrl?: string
  location?: string
  vibeLine?: string
  followsViewer?: boolean
  isFollowing?: boolean
  isAnonymous?: boolean
  /** True when this row represents the logged-in viewer themselves.
   * Suppresses the FollowTag — you can't follow yourself. */
  isViewer?: boolean
  onFollowToggle?: (uid: string, currentlyFollowing: boolean) => void
  /** i18n labels — passed from parent (ConnectionsModal) which holds the staticText HOC.
   * Follow/Following/Unfollow labels on the pill itself come from the shared
   * FollowTag component, which reads its own staticText entries. */
  anonymousLabel?: string
  followsYouLabel?: string
}

function ConnectionCard ({
  uid,
  uuid,
  displayName,
  avatarUrl,
  location,
  vibeLine,
  followsViewer = false,
  isFollowing = false,
  isAnonymous = false,
  isViewer = false,
  onFollowToggle,
  anonymousLabel = 'Anonymous',
  followsYouLabel = 'Follows you',
}: ConnectionCardProps) {
  const profileHref = `/profile/${uuid || uid}`

  // FollowTag exposes separate callbacks for the two transitions so
  // hover states know which label/icon/variant to show next. Thread both
  // through the single `onFollowToggle(uid, currentlyFollowing)` prop by
  // snapshotting the current following state at click time.
  const handleFollow = useCallback(() => {
    if (onFollowToggle) onFollowToggle(uid, false)
  }, [onFollowToggle, uid])

  const handleUnfollow = useCallback(() => {
    if (onFollowToggle) onFollowToggle(uid, true)
  }, [onFollowToggle, uid])

  // Anonymous users show no metadata
  const resolvedName = isAnonymous ? anonymousLabel : displayName
  const metaLine = isAnonymous ? '' : [location, vibeLine].filter(Boolean).join(' \u00b7 ')
  const renderImage = !isAnonymous && !!avatarUrl

  return (
    <div className={`connection-card${isAnonymous ? ' connection-card--anonymous' : ''}`}>
      <a href={isAnonymous ? undefined : profileHref} className="connection-card__avatar-link">
        {renderImage ? (
          <img className="connection-card__avatar" src={avatarUrl} alt={resolvedName} />
        ) : (
          <span className="connection-card__avatar-placeholder">
            {resolvedName.charAt(0).toUpperCase()}
          </span>
        )}
      </a>
      <div className="connection-card__info">
        <div className="connection-card__name-row">
          {isAnonymous ? (
            <span className="connection-card__name connection-card__name--anonymous">
              {resolvedName}
            </span>
          ) : (
            <a href={profileHref} className="connection-card__name">
              {resolvedName}
            </a>
          )}
        </div>
        {!isAnonymous && followsViewer && (
          <span className="connection-card__follows-you">
            <Check size="14px" color="currentColor" />
            {followsYouLabel}
          </span>
        )}
        {!isAnonymous && metaLine && (
          <span className="connection-card__meta">{metaLine}</span>
        )}
      </div>
      {!isAnonymous && !isViewer && (
        <span className="connection-card__follow-tag">
          <FollowTag
            isFollowing={isFollowing}
            followerCount={0}
            onClick={handleFollow}
            onUnfollow={handleUnfollow}
          />
        </span>
      )}
    </div>
  )
}

export const COMPONENT_NAME = 'ConnectionCard'

export default React.memo(ConnectionCard)
